import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom'
import * as customersApi from '@/api/customers'
import { useAuth } from '@/auth/AuthContext'
import { AppShell } from '@/components/AppShell'
import { CategoryIcon } from '@/components/CategoryIcon'
import { SettingsNav } from '@/components/SettingsNav'
import { childrenOf, topLevelCategories } from '@/lib/categories'
import { useSiteSettings } from '@/siteSettingsContext'

export function CustomerLayout() {
  const { customerId, slug } = useParams()
  const { canEdit, isAdmin } = useAuth()
  const { customerLabel } = useSiteSettings()
  const navigate = useNavigate()
  const [customer, setCustomer] = useState(null)
  const [categories, setCategories] = useState([])
  const [customerOptions, setCustomerOptions] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  // Parent slugs that are expanded; empty = all collapsed by default
  const [expandedParents, setExpandedParents] = useState(() => new Set())

  const reload = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [c, cats] = await Promise.all([
        customersApi.getCustomer(customerId),
        customersApi.listCategories(customerId),
      ])
      setCustomer(c)
      setCategories(cats)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not load customer.',
      )
      setCustomer(null)
      setCategories([])
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => {
    reload()
  }, [reload])

  useEffect(() => {
    let cancelled = false
    async function loadOptions() {
      try {
        const list = await customersApi.listCustomers()
        if (!cancelled) {
          setCustomerOptions(
            list
              .filter((c) => c.is_active !== false)
              .sort((a, b) => a.name.localeCompare(b.name)),
          )
        }
      } catch {
        if (!cancelled) setCustomerOptions([])
      }
    }
    loadOptions()
    return () => {
      cancelled = true
    }
  }, [])

  const rootCategories = useMemo(() => topLevelCategories(categories), [categories])

  // When the open page is a sub-category, expand its parent so it stays visible
  useEffect(() => {
    if (!slug) return
    const current = categories.find((c) => c.slug === slug)
    if (current?.parent_slug) {
      setExpandedParents((prev) => {
        if (prev.has(current.parent_slug)) return prev
        const next = new Set(prev)
        next.add(current.parent_slug)
        return next
      })
    }
  }, [slug, categories])

  // Reset collapse state when switching customer
  useEffect(() => {
    setExpandedParents(new Set())
  }, [customerId])

  function toggleParent(parentSlug) {
    setExpandedParents((prev) => {
      const next = new Set(prev)
      if (next.has(parentSlug)) next.delete(parentSlug)
      else next.add(parentSlug)
      return next
    })
  }

  function switchCustomer(nextId) {
    if (!nextId || String(nextId) === String(customerId)) return
    if (slug && slug !== 'manage-categories') {
      navigate(`/customers/${nextId}/${slug}`)
    } else {
      navigate(`/customers/${nextId}`)
    }
  }

  async function toggleHidden(slugName, currentlyHidden) {
    try {
      const updated = await customersApi.setCategoryHidden(
        customerId,
        slugName,
        !currentlyHidden,
      )
      setCategories((prev) =>
        prev.map((cat) => (cat.slug === slugName ? { ...cat, ...updated } : cat)),
      )
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not update page visibility.',
      )
    }
  }

  if (loading) {
    return (
      <AppShell>
        <p className="text-brand-muted">Loading…</p>
      </AppShell>
    )
  }

  if (!customer) {
    return (
      <AppShell>
        <p className="text-red-700">{error || 'Customer not found.'}</p>
        <button
          type="button"
          className="mt-4 text-sm underline"
          onClick={() => navigate('/')}
        >
          Back to customers
        </button>
      </AppShell>
    )
  }

  const sidebar = (
    <>
      <div className="shrink-0 border-b border-sidebar-border px-4 py-3">
        <p className="text-xs font-medium uppercase tracking-wider text-sidebar-foreground/50">
          {customerLabel}
        </p>
        {isAdmin || customerOptions.length > 1 ? (
          <label className="mt-1 block">
            <span className="sr-only">Select {customerLabel.toLowerCase()}</span>
            <select
              value={String(customer.id)}
              onChange={(e) => switchCustomer(e.target.value)}
              className="mt-0.5 w-full rounded-md border border-sidebar-border bg-sidebar-accent px-2 py-1.5 text-sm font-semibold text-sidebar-foreground outline-none focus:ring-1 focus:ring-primary"
            >
              {customerOptions.length === 0 && (
                <option value={String(customer.id)}>{customer.name}</option>
              )}
              {customerOptions.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="mt-1 font-semibold text-sidebar-foreground">{customer.name}</p>
        )}
      </div>
      <nav className="min-h-0 flex-1 overflow-y-auto p-2">
        <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-sidebar-foreground/50">
          Categories
        </p>
        {rootCategories.map((cat) => {
          const kids = childrenOf(categories, cat.slug)
          const hasKids = kids.length > 0
          const isExpanded = hasKids && expandedParents.has(cat.slug)
          return (
            <div key={cat.slug} className="mb-0.5">
              <div className="flex items-stretch">
                {hasKids ? (
                  <button
                    type="button"
                    aria-label={isExpanded ? `Collapse ${cat.title}` : `Expand ${cat.title}`}
                    aria-expanded={isExpanded}
                    onClick={() => toggleParent(cat.slug)}
                    className="flex w-7 shrink-0 items-center justify-center text-sidebar-foreground/50 hover:text-white"
                  >
                    <svg
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className={`h-3.5 w-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      aria-hidden
                    >
                      <path d="M7.293 14.707a1 1 0 0 1 0-1.414L10.586 10 7.293 6.707a1 1 0 0 1 1.414-1.414l4 4a1 1 0 0 1 0 1.414l-4 4a1 1 0 0 1-1.414 0z" />
                    </svg>
                  </button>
                ) : (
                  <span className="w-7 shrink-0" aria-hidden />
                )}
                <NavLink
                  to={`/customers/${customerId}/${cat.slug}`}
                  className={({ isActive }) =>
                    [
                      'flex min-w-0 flex-1 items-center gap-2.5 border-l-[3px] border-transparent py-2 pr-3 text-sm font-medium transition-colors',
                      isActive
                        ? 'border-white bg-white/12 font-semibold text-white'
                        : 'text-sidebar-foreground/70 hover:bg-white/8 hover:text-white',
                      cat.hidden_from_customer ? 'opacity-60' : '',
                    ].join(' ')
                  }
                >
                  <CategoryIcon name={cat.icon || 'document'} className="h-4 w-4 shrink-0 opacity-90" />
                  <span className="min-w-0 truncate">
                    {cat.title}
                    {cat.hidden_from_customer && canEdit && (
                      <span className="ml-1 text-xs text-amber-400">(hidden)</span>
                    )}
                  </span>
                </NavLink>
              </div>
              {hasKids && isExpanded && (
                <div className="mb-1 ml-9 border-l-2 border-white/25 pl-3">
                  {kids.map((child) => (
                    <NavLink
                      key={child.slug}
                      to={`/customers/${customerId}/${child.slug}`}
                      className={({ isActive }) =>
                        [
                          'mb-0.5 flex items-center gap-2 rounded-r-md border-l-[3px] border-transparent py-1.5 pl-2.5 pr-3 text-[13px] font-medium transition-colors',
                          isActive
                            ? 'border-white bg-white/12 font-semibold text-white'
                            : 'text-sidebar-foreground/40 hover:bg-white/8 hover:text-white/90',
                          child.hidden_from_customer ? 'opacity-60' : '',
                        ].join(' ')
                      }
                    >
                      <CategoryIcon
                        name={child.icon || 'document'}
                        className="h-3.5 w-3.5 shrink-0 opacity-90"
                      />
                      <span className="min-w-0 truncate">
                        {child.title}
                        {child.hidden_from_customer && canEdit && (
                          <span className="ml-1 text-xs text-amber-400">(hidden)</span>
                        )}
                      </span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>
      {isAdmin && (
        <div className="mt-auto shrink-0 border-t border-sidebar-border">
          <SettingsNav customerId={customerId} customerName={customer.name} />
        </div>
      )}
    </>
  )

  return (
    <AppShell title={customer.name} sidebar={sidebar}>
      {error && (
        <p className="mb-4 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
      <Outlet
        context={{
          customer,
          setCustomer,
          categories,
          setCategories,
          canEdit,
          isAdmin,
          toggleHidden,
          reload,
        }}
      />
    </AppShell>
  )
}
